import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { G } from "@/brand";
import api from "@/lib/api";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { PromptDetail } from "@/components/PromptDetail";
import { FeedbackModal } from "@/components/FeedbackModal";
import { useAuth } from "@/context/AuthContext";
import { Search, Lock, Unlock, Sparkles, MessageSquarePlus, Star } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "free", label: "Free" },
  { id: "upgrade_1", label: "Vault" },
  { id: "upgrade_2", label: "Codex" },
];

export default function Library() {
  const { user, requireAuth } = useAuth();
  const nav = useNavigate();
  const [genres, setGenres] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [genre, setGenre] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fb, setFb] = useState(false);
  const [favs, setFavs] = useState(new Set());
  const [previewTier, setPreviewTier] = useState(""); // "" = full admin; else preview a tier
  const isAdmin = user?.role === "admin";

  useEffect(() => { api.get("/genres").then(({ data }) => setGenres(data.genres)); }, []);

  useEffect(() => {
    if (user?.email) api.get("/favorites").then(({ data }) => setFavs(new Set(data.map((f) => f.prompt_id)))).catch(() => {});
    else setFavs(new Set());
  }, [user]);

  const toggleFav = async (e, p) => {
    e.stopPropagation();
    if (!requireAuth("Sign in to save favorite prompts.")) return;
    const isFav = favs.has(p.id);
    setFavs((s) => { const n = new Set(s); isFav ? n.delete(p.id) : n.add(p.id); return n; });
    try {
      if (isFav) await api.delete(`/favorites/${p.id}`);
      else await api.post("/favorites", { prompt_id: p.id });
    } catch { /* revert on error */ setFavs((s) => { const n = new Set(s); isFav ? n.add(p.id) : n.delete(p.id); return n; }); }
  };

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (genre) params.genre = genre;
    if (search) params.search = search;
    if (isAdmin && previewTier) params.preview_tier = previewTier;
    const t = setTimeout(() => {
      api.get("/prompts", { params }).then(({ data }) => { setPrompts(data.prompts); setLoading(false); });
    }, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [genre, search, user, previewTier, isAdmin]);

  const shown = prompts.filter((p) => filter === "all" || p.access_level === filter);

  const levelColor = (lvl) => lvl === "upgrade_2" ? G.orange : lvl === "upgrade_1" ? G.gold : G.goldDim;
  const levelTag = (lvl) => lvl === "upgrade_2" ? "CODEX" : lvl === "upgrade_1" ? "VAULT" : "FREE";

  return (
    <div style={{ background: G.black, minHeight: "100vh" }}>
      <Nav />
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "130px 5vw 60px" }}>
        <div className="mono" style={{ fontSize: 9, letterSpacing: "0.5em", color: G.gold, marginBottom: 14 }}>THE PROMPT LIBRARY</div>
        <h1 style={{ fontSize: "clamp(32px,5vw,56px)", fontWeight: 300, fontStyle: "italic", color: "#fff", marginBottom: 28 }}>
          50 genres. Every sound.
        </h1>

        {/* Admin: preview as tier */}
        {isAdmin && (
          <div data-testid="admin-preview-bar" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 20, border: `1px solid ${G.orange}55`, background: "#16130a", padding: "12px 14px" }}>
            <span className="mono" style={{ fontSize: 9, letterSpacing: ".2em", color: G.orange }}>ADMIN · PREVIEW AS</span>
            {[{ id: "", label: "Admin (Full)" }, { id: "free", label: "Free" }, { id: "vault", label: "Vault" }, { id: "codex", label: "Codex" }].map((v) => (
              <button key={v.id || "admin"} data-testid={`preview-${v.id || "admin"}`} onClick={() => setPreviewTier(v.id)} className="mono"
                style={{ padding: "7px 14px", fontSize: 9, letterSpacing: ".1em", cursor: "pointer", textTransform: "uppercase",
                  background: previewTier === v.id ? G.orange : "transparent", color: previewTier === v.id ? G.black : G.muted,
                  border: `1px solid ${previewTier === v.id ? G.orange : G.border}` }}>
                {v.label}
              </button>
            ))}
            <span className="mono" style={{ fontSize: 9, color: G.goldDim, letterSpacing: ".08em", marginLeft: "auto" }}>
              {previewTier ? "Seeing exactly what this tier sees" : "Full access — all unlocked"}
            </span>
          </div>
        )}

        {/* Search */}
        <div style={{ position: "relative", maxWidth: 520, marginBottom: 20 }}>
          <Search size={16} style={{ position: "absolute", left: 16, top: 15, color: G.goldDim }} />
          <input data-testid="library-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search genres, styles, tags…"
            style={{ width: "100%", padding: "13px 16px 13px 44px", background: G.dark, border: `1px solid ${G.border}`, color: G.text, fontSize: 15 }} />
        </div>

        {/* Tier filter */}
        <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
          {FILTERS.map((f) => (
            <button key={f.id} data-testid={`filter-${f.id}`} onClick={() => setFilter(f.id)} className="mono var-tab"
              style={{ padding: "8px 16px", fontSize: 9, letterSpacing: ".12em", cursor: "pointer", textTransform: "uppercase",
                background: filter === f.id ? G.gold : "transparent", color: filter === f.id ? G.black : G.muted, border: `1px solid ${filter === f.id ? G.gold : G.border}` }}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Genre chips */}
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 12, marginBottom: 24 }}>
          <button onClick={() => setGenre("")} className="mono" style={chip(genre === "")}>All Genres</button>
          {genres.map((g) => (
            <button key={g.id} data-testid={`genre-${g.id}`} onClick={() => setGenre(g.id)} className="mono" style={{ ...chip(genre === g.id), whiteSpace: "nowrap" }}>
              {g.name}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}><div className="spinner" /></div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {shown.map((p) => {
              const locked = p.access_level !== "free" && !p.has_access;
              return (
                <div key={p.id} data-testid={`prompt-row-${p.id}`} className="genre-row" onClick={() => setActive(p)}
                  style={{ background: G.dark, border: `1px solid ${G.border}55`, padding: "16px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 17, color: "#fff", fontWeight: 400 }}>{p.genre_name}</span>
                      <span className="mono" style={{ fontSize: 8, letterSpacing: ".15em", color: levelColor(p.access_level), border: `1px solid ${levelColor(p.access_level)}55`, padding: "2px 7px" }}>{levelTag(p.access_level)}</span>
                      <span className="mono" style={{ fontSize: 9, color: G.goldDim, letterSpacing: ".1em" }}>{p.category} · {p.revision}</span>
                      {p.unlocked && p.access_level !== "free" && <span className="mono" style={{ fontSize: 8, color: G.gold, letterSpacing: ".1em" }}>✓ OWNED</span>}
                      {locked && <span data-testid={`cost-${p.id}`} className="mono" style={{ fontSize: 8, letterSpacing: ".1em", color: levelColor(p.access_level) }}>🔒 5 CR TO OPEN</span>}
                    </div>
                    <div style={{ fontSize: 13.5, color: G.muted, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {locked ? "Locked premium prompt — upgrade to reveal the full codex-grade specification." : p.preview}
                    </div>
                  </div>
                  <button data-testid={`fav-${p.id}`} onClick={(e) => toggleFav(e, p)} title="Save to favorites"
                    style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, padding: 4, display: "flex" }}>
                    <Star size={16} color={favs.has(p.id) ? G.gold : G.goldDim} fill={favs.has(p.id) ? G.gold : "none"} />
                  </button>
                  {locked
                    ? <Lock size={16} color={levelColor(p.access_level)} style={{ flexShrink: 0 }} />
                    : <Unlock size={16} color={G.goldDim} style={{ flexShrink: 0 }} />}
                </div>
              );
            })}
            {shown.length === 0 && <div style={{ color: G.muted, fontStyle: "italic", textAlign: "center", padding: 40 }}>No prompts match.</div>}
          </div>
        )}

        {!user?.email && (
          <div style={{ marginTop: 40, border: `1px solid ${G.border}`, padding: 28, background: G.dark, textAlign: "center" }}>
            <Sparkles size={22} color={G.gold} style={{ margin: "0 auto 10px" }} />
            <div style={{ fontSize: 20, color: "#fff", fontStyle: "italic", marginBottom: 8 }}>Unlock 2,050–10,050 premium prompts</div>
            <div style={{ color: G.muted, fontStyle: "italic", marginBottom: 18 }}>Vault & Codex add deep per-genre variations + in-app AI editing.</div>
            <button data-testid="library-upgrade-cta" onClick={() => nav("/pricing")} className="btn-gold" style={{ padding: "13px 30px", fontSize: 10 }}>View Upgrades</button>
          </div>
        )}
      </div>

      <button data-testid="open-feedback" onClick={() => setFb(true)} className="btn-gold" style={{ position: "fixed", bottom: 24, right: 24, zIndex: 300, padding: "12px 18px", fontSize: 10, display: "flex", alignItems: "center", gap: 8 }}>
        <MessageSquarePlus size={14} /> Suggest
      </button>
      <FeedbackModal open={fb} onClose={() => setFb(false)} />
      {active && <PromptDetail prompt={active} previewTier={isAdmin ? previewTier : ""} onClose={() => setActive(null)} />}
      <Footer />
    </div>
  );
}

const chip = (on) => ({ padding: "8px 14px", fontSize: 9, letterSpacing: ".1em", cursor: "pointer", textTransform: "uppercase",
  background: on ? G.goldGhost : "transparent", color: on ? G.gold : G.muted, border: `1px solid ${on ? G.gold : G.border}55` });
